import { useCallback, useMemo } from "react"

import { CACHE_PROFILES } from "@/config/query-cache"
import { normalizeTags } from "@/tags/values"
import {
  useTransportMutation,
  useTransportQuery,
  useTransportQueryUtils,
} from "@/transport"

import type { ListRunTemplatesTransportRequest } from "@/types_api/backend/transport/list_run_templates_transport_request"
import type { RunTemplateResource } from "@/types_api/backend/transport/list_run_templates_transport_response"
import type { RunTemplateRecord } from "@/types/api/RunTemplateRecord"

import type {
  RunTemplateCreateInput,
  RunTemplateUpdateInput,
} from "../utils/runTemplatePayload"

interface DeleteTemplateOptions {
  invalidate?: boolean
}

export type TemplatesListData = {
  id: string
  value: string
  label: string
  description: string
}[]

export interface TemplatesAllData {
  templates: RunTemplateRecord[]
}

interface UseTemplatesTransportOptions<TData, TSelected> {
  enabled?: boolean
  select?: (data: TData) => TSelected
  tagFilters?: string[]
}

function buildRunConfig(
  resource: RunTemplateResource,
): RunTemplateRecord["run_config"] {
  const options: RunTemplateRecord["run_config"]["options"] = {
    concurrency: resource.concurrency ?? 1,
    iteration_count: resource.iteration_count ?? 1,
    sub_sample_size: resource.sub_sample_size ?? 0,
  }

  if (resource.sub_sample_seed !== undefined) {
    options.sub_sample_seed = resource.sub_sample_seed
  }

  if (resource.test_case_ids?.length) {
    options.test_case_ids = resource.test_case_ids
  }

  const runConfig: RunTemplateRecord["run_config"] = {
    options,
  }

  if (resource.metric_ids?.length) {
    runConfig.metric_ids = resource.metric_ids
  }

  if (resource.mutation_ids?.length) {
    runConfig.mutation_ids = resource.mutation_ids
  }

  if (resource.metadata) {
    runConfig.metadata =
      resource.metadata as RunTemplateRecord["run_config"]["metadata"]
  }

  if (resource.dev_id !== undefined) {
    runConfig.dev_id = resource.dev_id
  }

  return runConfig
}

function mapRunTemplateResourceToRecord(
  resource: RunTemplateResource,
): RunTemplateRecord {
  const runConfig = buildRunConfig(resource)

  return {
    attribution: resource.attribution,
    id: resource.id,
    display_name: resource.display_name,
    description: resource.description ?? "",
    agent_ids: resource.agent_ids ?? [],
    persona_ids: resource.persona_ids ?? [],
    test_set_ids: resource.test_set_ids ?? [],
    metric_ids: resource.metric_ids ?? [],
    mutation_ids: resource.mutation_ids ?? [],
    iteration_count: resource.iteration_count ?? 1,
    concurrency: resource.concurrency ?? 1,
    sub_sample_size: resource.sub_sample_size ?? 0,
    sub_sample_seed: resource.sub_sample_seed ?? undefined,
    test_case_ids: resource.test_case_ids ?? undefined,
    dev_id: resource.dev_id ?? undefined,
    metadata: resource.metadata as RunTemplateRecord["metadata"] | undefined,
    run_config: runConfig,
    tags: resource.tags ?? [],
    created_at: resource.create_time ?? resource.update_time ?? "",
    updated_at: resource.update_time ?? resource.create_time ?? "",
    created_by_user_id: resource.created_by_user_id ?? null,
  }
}

function useListRunTemplatesRequest(tagFilters?: string[]) {
  const normalizedTagFilters = useMemo(
    () => normalizeTags(tagFilters ?? []),
    [tagFilters],
  )

  return useMemo<ListRunTemplatesTransportRequest>(
    () => ({
      tag_filters:
        normalizedTagFilters.length > 0
          ? (normalizedTagFilters as ListRunTemplatesTransportRequest["tag_filters"])
          : undefined,
    }),
    [normalizedTagFilters],
  )
}

export function useRunTemplateTransport<T = RunTemplateRecord>(
  templateId: string,
  options?: {
    enabled?: boolean
    refetchOnMount?: boolean | "always"
    select?: (data: RunTemplateRecord) => T
  },
) {
  const { enabled = true, refetchOnMount, select } = options ?? {}

  return useTransportQuery({
    operation: "getRunTemplate",
    request: { run_template_id: templateId },
    options: {
      ...CACHE_PROFILES.lookup,
      enabled: enabled && !!templateId && templateId !== "new",
      refetchOnMount: refetchOnMount ?? CACHE_PROFILES.lookup.refetchOnMount,
      select: (response) => {
        const template = mapRunTemplateResourceToRecord(response.run_template)
        return select ? select(template) : (template as T)
      },
    },
  })
}

export function useTemplatesListTransport<T = TemplatesListData>(
  options?: UseTemplatesTransportOptions<TemplatesListData, T>,
) {
  const { enabled, select, tagFilters } = options ?? {}
  const request = useListRunTemplatesRequest(tagFilters)

  return useTransportQuery({
    operation: "listRunTemplates",
    request: request as never,
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
      select: (response) => {
        const templateOptions = response.template_options
        return select ? select(templateOptions) : (templateOptions as T)
      },
    },
  })
}

export function useTemplatesAllTransport<T = TemplatesAllData>(
  options?: UseTemplatesTransportOptions<TemplatesAllData, T>,
) {
  const { enabled, select, tagFilters } = options ?? {}
  const request = useListRunTemplatesRequest(tagFilters)

  return useTransportQuery({
    operation: "listRunTemplates",
    request: request as never,
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
      select: (response) => {
        const templatesData = {
          templates: response.templates.map(mapRunTemplateResourceToRecord),
        }

        return select ? select(templatesData) : (templatesData as T)
      },
    },
  })
}

export function useRunTemplateTransportMutations() {
  const transportUtils = useTransportQueryUtils()

  const invalidateTemplates = useCallback(
    async (templateId?: string) => {
      const invalidations = [
        transportUtils.invalidateOperation("listRunTemplates"),
      ]
      if (templateId) {
        invalidations.push(
          transportUtils.invalidate("getRunTemplate", {
            run_template_id: templateId,
          }),
        )
      }
      await Promise.all(invalidations)
    },
    [transportUtils],
  )

  const createMutation = useTransportMutation("createRunTemplate", {
    onSuccess: async (response) => {
      await invalidateTemplates(response.run_template.id)
    },
  })
  const updateMutation = useTransportMutation("updateRunTemplate", {
    onSuccess: async (response) => {
      await invalidateTemplates(response.run_template.id)
    },
  })
  const deleteMutation = useTransportMutation("deleteRunTemplate")
  const {
    error: createError,
    isPending: createIsPending,
    mutateAsync: createRunTemplate,
  } = createMutation
  const {
    error: updateError,
    isPending: updateIsPending,
    mutateAsync: updateRunTemplate,
  } = updateMutation
  const {
    error: deleteError,
    isPending: deleteIsPending,
    mutateAsync: deleteRunTemplate,
  } = deleteMutation

  const createTemplate = useCallback(
    async (input: RunTemplateCreateInput) => {
      const response = await createRunTemplate(input)
      return mapRunTemplateResourceToRecord(response.run_template)
    },
    [createRunTemplate],
  )

  const updateTemplate = useCallback(
    async (input: RunTemplateUpdateInput) => {
      const response = await updateRunTemplate(input)
      return mapRunTemplateResourceToRecord(response.run_template)
    },
    [updateRunTemplate],
  )

  const deleteTemplate = useCallback(
    async (runTemplateId: string, options?: DeleteTemplateOptions) => {
      await deleteRunTemplate({ run_template_id: runTemplateId })
      if (options?.invalidate ?? true) {
        await invalidateTemplates()
      }
    },
    [deleteRunTemplate, invalidateTemplates],
  )

  return useMemo(
    () => ({
      createTemplate,
      createError,
      createIsPending,
      deleteError,
      deleteIsPending,
      deleteTemplate,
      invalidateTemplates,
      updateError,
      updateIsPending,
      updateTemplate,
    }),
    [
      createError,
      createIsPending,
      createTemplate,
      deleteError,
      deleteIsPending,
      deleteTemplate,
      invalidateTemplates,
      updateError,
      updateIsPending,
      updateTemplate,
    ],
  )
}
